import { useState } from "react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useExtendContract } from "@/hooks/queries/useExtendContract";
import {
  toAdminOrgError,
  type AdminOrgBanner,
} from "@/routes/admin/admin-orgs-error";
import { requiredDate } from "@/routes/admin/create-organization-schema";
import { RENEW_CONTRACT_TRIGGER_LABEL } from "@/routes/admin/RenewContractControl";

// Story 15.3 — the Contract card's `contractDateControl` slot on Screen 24.
//
// THIS CONTROL IS A MID-TERM CORRECTION. It moves `contract_end_date` and
// nothing else: the term boundary does not move with it, the seat pool is not
// touched, and consumption is not re-established. Use it when the stored date
// is simply wrong or the current term is being stretched.
//
// IT IS NOT A RENEWAL, and this header used to say it was (corrected by Story
// 21.1, FR87). The superseded claim was that "a renewal is an UPDATE IN PLACE on
// the single contract pair (FR74)" reached through this one column. That was
// true of the product until FR87 gave a term boundary its own write: renewing
// now closes a term and opens the next with its own seat count, through
// `renew_contract` in the card's third slot. Extending an end date does neither.
//
// The FR74 half that survives is the SHAPE of this write: one column, one audit
// row, no history table, no rollover, no proration. The control therefore
// renders no renewal list and no operations log (AC12) — the timeline the mockup
// draws at `:97` is 15.7's and rests on a predicate the client has not answered.
//
// IT SUBMITS ON ITS OWN, never through the card header's `Save changes` (that
// button is 15.6's `update-details` write). Folding this POST into a page-level
// save would make one button answer under two endpoints' capabilities and five
// error codes, and a half-succeeded save could not report which field refused.
//
// THE BODY CARRIES `contract_end_date` AND NOTHING ELSE (AC10). The start date is
// echoed by the response but never written from here.
//
// THE CLIENT DOES NOT OWN THE CLOCK. There is no client-side past-date refusal
// and no client mirror of the server's `le=9000-12-31`: the server compares in
// UTC (`extend_contract/command.py:123`) and a browser-local comparison disagrees
// with it by up to a day at the boundary, refusing dates the server accepts. Both
// refusals land inline through the shared mapper instead — ORG-005 by code, the
// upper bound by `loc` (it is a schema-level 422 with no code at all).
//
// AN EXPIRED ORGANIZATION USES THIS SAME CONTROL (AC5): the shipped endpoint has
// one path, and a lapsed contract is an ordinary extension. There is no
// "reactivate" affordance, because advertising one would name a mode the server
// does not have. Renewing a lapsed organization is the OTHER control's job and
// answers a different question — this one stretches the term that lapsed, that
// one opens a fresh term after it.

export const EXTEND_CONTRACT_TRIGGER_LABEL = "Extend contract end date";
export const EXTEND_CONTRACT_SUBMIT_LABEL = "Save contract end date";
export const EXTEND_CONTRACT_PENDING_LABEL = "Saving\u2026";
export const EXTEND_CONTRACT_FIELD_LABEL = "New contract end date";
/**
 * The description says what the WRITE is, and stops there.
 *
 * Story 21.1 AC28 replaced it. The superseded sentence — *"Extending the end
 * date renews the contract in place, on this same record."* — asserted the rule
 * FR87 overturned, and it asserted it on the dialog an operator is looking at
 * while deciding between two controls, which is the worst place for it to be
 * wrong. It must describe a mid-term correction and must not claim to renew.
 *
 * It does NOT restate the "no rollover, no credits, no proration" sentence
 * (W3 row 7): `ContractCard` already renders that once, on the very card this
 * control sits in, and a second copy is one that drifts — it had already drifted
 * to "no credit" before this was caught. For the same reason it does not restate
 * the card's renewal sentence either; it names the other control by its trigger
 * label and leaves the card to explain it.
 *
 * That label is INTERPOLATED, not retyped. This sentence sends the operator to a
 * button, so the two strings have to match exactly or the instruction points at
 * nothing — a renamed trigger would otherwise leave this pointing at a control
 * that no longer answers to the name.
 */
export const EXTEND_CONTRACT_DESCRIPTION =
  `Corrects the end date within the current term. It does not open a new term or change the seat count — use ${RENEW_CONTRACT_TRIGGER_LABEL} for that.`;
export const EXTEND_CONTRACT_DATE_REQUIRED_MESSAGE = "Enter the new contract end date.";

// `requiredDate` is imported from the create-organization form's schema AS IT
// STANDS (boundary D-c): one date grammar with two client sources is one that
// drifts. It supplies both messages this control can raise client-side — the
// missing-value one passed in here, and its own "Enter a valid date." for a
// value that is not `YYYY-MM-DD`.
const endDateShape = requiredDate(EXTEND_CONTRACT_DATE_REQUIRED_MESSAGE);

export function ExtendContractControl({
  organizationId,
  contractEndDate,
}: {
  organizationId: string;
  /** The stored end date, used to pre-fill the field. May be in the past (AC5). */
  contractEndDate: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(contractEndDate ?? "");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<AdminOrgBanner | null>(null);
  const extendContract = useExtendContract(organizationId);

  function openDialog() {
    setDraft(contractEndDate ?? "");
    setValidationError(null);
    setFieldError(null);
    setBanner(null);
    setOpen(true);
  }

  /**
   * THE DIALOG DOES NOT CLOSE WHILE THE EXTENSION IS IN FLIGHT.
   *
   * Copied from `RenewContractControl.closeDialog` because Franklin#828 made
   * this path refuse (DW-765). An unmounted dialog takes that refusal with it
   * and the operator is told nothing about a write they have already started.
   *
   * `onSuccess` still closes with `setOpen(false)` directly — a close on
   * success is not a dismissal, and the mutate-level callback already runs
   * after `isPending` has cleared.
   */
  function closeDialog() {
    if (extendContract.isPending) return;
    setOpen(false);
  }

  function onSubmit() {
    if (extendContract.isPending) return;

    const parsed = endDateShape.safeParse(draft);
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? EXTEND_CONTRACT_DATE_REQUIRED_MESSAGE);
      setFieldError(null);
      setBanner(null);
      return;
    }

    setValidationError(null);
    setFieldError(null);
    setBanner(null);
    extendContract.mutate(
      { contract_end_date: parsed.data },
      {
        onSuccess: () => setOpen(false),
        onError: (error) => {
          const mapped = toAdminOrgError(error);
          setFieldError(mapped.fieldErrors.contractEndDate ?? null);
          setBanner(mapped.banner);
        },
      },
    );
  }

  const inlineError = validationError ?? fieldError;

  return (
    <>
      <Button variant="secondary" size="sm" onClick={openDialog}>
        {EXTEND_CONTRACT_TRIGGER_LABEL}
      </Button>

      <Dialog open={open} onOpenChange={(next) => (next ? openDialog() : closeDialog())}>
        <DialogContent className="w-[min(var(--width-md,520px),100%)]">
          <DialogHeader>
            <DialogTitle>{EXTEND_CONTRACT_TRIGGER_LABEL}</DialogTitle>
            <DialogDescription>{EXTEND_CONTRACT_DESCRIPTION}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1">
            <Label htmlFor="extend-contract-end-date">{EXTEND_CONTRACT_FIELD_LABEL}</Label>
            <Input
              id="extend-contract-end-date"
              type="date"
              value={draft}
              aria-invalid={inlineError ? true : undefined}
              aria-describedby={inlineError ? "extend-contract-end-date-error" : undefined}
              onChange={(event) => {
                setDraft(event.target.value);
                setValidationError(null);
                setFieldError(null);
              }}
            />
            {inlineError ? (
              <p
                id="extend-contract-end-date-error"
                role="alert"
                className="text-sm text-[color:var(--text-error)]"
              >
                {inlineError}
              </p>
            ) : null}
          </div>

          {banner ? (
            <Banner variant={banner.variant} title={banner.title}>
              <p>{banner.body}</p>
              {banner.code ? (
                <p className="text-sm">{`Reference code: ${banner.code}`}</p>
              ) : null}
            </Banner>
          ) : null}

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={closeDialog}
              disabled={extendContract.isPending}
            >
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={extendContract.isPending}>
              {extendContract.isPending
                ? EXTEND_CONTRACT_PENDING_LABEL
                : EXTEND_CONTRACT_SUBMIT_LABEL}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
